import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ArrowLeft, X } from 'lucide-react';
import ReelListenButton from '../components/ReelListenButton';
import '../styles/OurStory.css';

const storyArcs = [
  {
    id: 'conflict',
    label: 'The Why \u2014 Anomalies Normalized',
    paragraphs: [
      "We were both serving in military medicine\u2014one as a nurse practitioner, one as a respiratory technician and healthcare administrator. We were proud of our service. But we kept witnessing the same quiet tragedy: patients leaving appointments feeling unheard, dismissed, or just unsure if anyone had really looked at the whole picture.",
      "We still remember the woman who came in carrying three folders of records\u2014years of labs, imaging reports, consult notes from specialists whose names she could barely recall. She had organized everything herself. She had done everything right. And she still didn\u2019t have an\u00A0answer.",
      "We felt the weight of\u00A0it."
    ]
  },
  {
    id: 'fatigue',
    label: 'The What \u2014 The Unheard Patient',
    highlight: "Chronic. Unresolved. Persistent.",
    paragraphs: [
      "What happens to the patient who doesn\u2019t have an advocate? The one who doesn\u2019t know how to request their records or what questions to ask? The one who senses something is wrong but keeps being told everything is\u00A0\u201Cnormal\u201D?",
      "Maybe you\u2019ve been that patient. Maybe you are right\u00A0now.",
      "Those questions gnawed at us. We knew there was a better way\u2014we\u2019d seen what\u2019s possible when you apply military precision and genuine attention to a problem. But the system wasn\u2019t built for\u00A0that."
    ]
  },
  {
    id: 'normalization',
    label: 'When & Who \u2014 Our Calling',
    highlight: "If Not Now, When? If Not Us, Then Who?",
    paragraphs: [
      "If we don\u2019t advocate for the sickest, the most tired, the most dismissed\u2014who will? \u201CIf this were our own family member, we wouldn\u2019t accept this. So why are we accepting it for everyone\u00A0else?\u201D",
      "We stopped waiting for the system to change. We looked at each other and said what we\u2019d been thinking for years.",
      "We decided to build the experience we\u2019d want for the people we love. A place where experts actually read the pages that matter\u2014connecting the dots across years of fragmented care\u2014and hand you back clarity in plain\u00A0language.",
      "Not another prescription. Not another rushed visit. Just a clear, written path\u00A0forward."
    ]
  },
  {
    id: 'commonNotRight',
    label: 'But How \u2014 Long Overdue,|Now Delivered',
    highlight: "Long-awaited. Well-deserved. Finally delivered.",
    paragraphs: [
      "CADENCE Medical Consulting exists to be the advocate every patient deserves. We bring 36+ years of combined military medical experience to a simple, radical idea: you can\u2019t make good decisions about your health unless you have the full\u00A0story.",
      "We find the story hiding in your records, and we give it back to\u00A0you."
    ],
    quote: "\u201CI\u2019ve seen what happens when complex conditions get simplified into standard protocols. At CADENCE, we take the time to understand your unique story\u2014and we don\u2019t stop until we\u2019ve connected every page of\u00A0it.\u201D",
    quoteAttr: "\u2014 Dr. Lakia S. Stewart, Chief Medical Officer & Co-Founder"
  }
];

const OurStory = () => {
  // Cinematic reel: one chapter active at a time. Null = the four
  // chapter "playbills" are shown on the velvet stage.
  const [activeIndex, setActiveIndex] = useState(null);
  // Internal animation key so each scene re-runs entry choreography
  // when advancing between chapters (without unmounting the overlay).
  const [sceneKey, setSceneKey] = useState(0);
  // Hands-free narration: once the visitor presses "Listen", `narrating`
  // stays true so each chapter auto-advances + auto-plays the next until
  // the reel ends or they navigate/close manually. `autoplay` is the
  // toggle in the Listen settings popover (persisted site-wide).
  const [narrating, setNarrating] = useState(false);
  const [autoplay, setAutoplay] = useState(() => {
    try {
      return localStorage.getItem('cadence_reel_autoplay') !== 'false';
    } catch {
      return true;
    }
  });
  const navigate = useNavigate();

  const toggleAutoplay = (val) => {
    setAutoplay(val);
    try {
      localStorage.setItem('cadence_reel_autoplay', val ? 'true' : 'false');
    } catch {
      /* ignore */
    }
  };

  const openChapter = (idx) => {
    setNarrating(false);
    setActiveIndex(idx);
    setSceneKey((k) => k + 1);
  };

  const closeReel = () => {
    setNarrating(false);
    setActiveIndex(null);
    // Return to the TOP of the page on close so the visitor lands back on
    // the Our Story intro (emblem + title + opening quote) rather than being
    // dumped deep at the chapter playbills (user feedback 2026-06:
    // "you've got to roll up to get back to the top — I don't like that").
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  // Reset the cinematic overlay's internal scroll to the top whenever a
  // new chapter opens — otherwise the visitor lands mid-page and feels
  // like they "snapped" to the middle (user feedback 2026-02). Mirrors
  // the same fix on the /demo reel. Targets multiple inner wrappers
  // because either can be the scrollable element depending on viewport.
  const scrollOverlayToTop = () => {
    requestAnimationFrame(() => {
      ['.story-reel-overlay', '.story-reel-scene', '.story-reel-body', '.story-reel-content']
        .forEach((sel) => {
          document.querySelectorAll(sel).forEach((el) => { el.scrollTop = 0; });
        });
    });
  };

  const nextChapter = () => {
    if (activeIndex === null) return;
    setNarrating(false);
    if (activeIndex >= storyArcs.length - 1) {
      // End of reel — return to the four playbills
      setActiveIndex(null);
    } else {
      setActiveIndex(activeIndex + 1);
      setSceneKey((k) => k + 1);
      scrollOverlayToTop();
    }
  };

  const prevChapter = () => {
    if (activeIndex === null || activeIndex === 0) return;
    setNarrating(false);
    setActiveIndex(activeIndex - 1);
    setSceneKey((k) => k + 1);
    scrollOverlayToTop();
  };

  // Narration for one chapter finished. In an autoplay session, advance
  // to the next chapter (which auto-plays on mount); otherwise the
  // session simply ends.
  const handleChapterEnded = () => {
    if (autoplay && activeIndex !== null && activeIndex < storyArcs.length - 1) {
      setActiveIndex(activeIndex + 1);
      setSceneKey((k) => k + 1);
      scrollOverlayToTop();
    } else {
      setNarrating(false);
    }
  };

  // Escape closes the reel; ArrowRight/Left navigate chapters
  useEffect(() => {
    if (activeIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeReel();
      else if (e.key === 'ArrowRight') nextChapter();
      else if (e.key === 'ArrowLeft') prevChapter();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  // Lock background scroll while reel is open
  useEffect(() => {
    if (activeIndex !== null) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [activeIndex]);

  // Click-anywhere-to-return-to-Founders. Only on the velvet background
  // — interactive children call stopPropagation so their own behaviour wins.
  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      navigate('/founders');
    }
  };

  const activeArc = activeIndex !== null ? storyArcs[activeIndex] : null;

  // Flatten a chapter into a clean narration string for the "Listen"
  // button — descriptor + highlight + paragraphs + founder quote. Smart
  // quotes and non-breaking spaces are stripped so Deepgram reads it
  // naturally rather than spelling out punctuation.
  const chapterNarration = (arc) => {
    if (!arc) return '';
    const parts = [];
    const cleanLabel = arc.label.replace('|', ' ');
    const descriptor = cleanLabel.includes(' \u2014 ')
      ? cleanLabel.split(' \u2014 ')[1]
      : null;
    if (descriptor) parts.push(`${descriptor}.`);
    if (arc.highlight) parts.push(arc.highlight);
    if (Array.isArray(arc.paragraphs)) parts.push(...arc.paragraphs);
    if (arc.quote) parts.push(arc.quote);
    return parts
      .join(' ')
      .replace(/[\u201C\u201D]/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  return (
    <div
      className="our-story-page"
      data-testid="our-story-page"
      onClick={handleBackgroundClick}
      role="presentation"
    >
      <Helmet>
        <title>Our Story | CADENCE Medical Consulting</title>
        <meta name="description" content="How two military medical veterans built CADENCE Medical Consulting after years of watching patients leave appointments feeling unheard, dismissed, and without answers." />
        <link rel="canonical" href="https://www.cadencemedicalconsulting.com/our-story" />
      </Helmet>

      {/* Emblem */}
      <section className="emblem-section">
        <div className="container">
          <div className="emblem-wrapper">
            <img
              src="/cadence-gold-foil.png"
              alt="CADENCE Medical Consulting"
              className="velvet-emblem-home"
            />
          </div>
        </div>
      </section>

      {/* Title */}
      <section className="unified-content-section">
        <div className="container">
          <h1 className="unified-page-title">Two Veterans.<br />One Conviction.</h1>
          <p className="story-page-subtitle">Our Story</p>
        </div>
      </section>

      {/* Opening Quote */}
      <section className="story-opening-quote">
        <p className="story-quote-text">
          &ldquo;If this were our family member, we wouldn&rsquo;t accept this.
          So why are we accepting it for everyone&nbsp;else?&rdquo;
        </p>
        <span className="story-quote-attr">&mdash; Lakia &amp; Andera, Founders</span>
      </section>

      {/* Founders Photo — 2026-02 swapped to the clinician+advocate
          shot in front of the caduceus emblem. The previous office
          photo on Our Story duplicated the Founders-page duo; this
          one tells a stronger visual story (clinical authority +
          patient advocacy) AND anchors the founders against a medical
          symbol that grounds the brand. Vignetted via .story-founders-img. */}
      <section className="story-founders-visual">
        <img
          src="/founders-cadence-emblem.png"
          alt="CADENCE Founders Andera and Lakia in front of the caduceus emblem at their consulting office"
          className="story-founders-img"
        />
      </section>

      {/* Four Chapter Playbills — tap one to play that scene. The
          plaque itself is also clickable and auto-opens Chapter 1
          (founder feedback 2026-02 "click the title to start at 1"). */}
      <section className="story-playbills-section" data-testid="story-playbills">
        <div className="container">
          <div className="story-playbills-intro-wrap">
            <button
              type="button"
              className="story-fivew-plaque story-fivew-plaque-clickable"
              onClick={() => openChapter(0)}
              aria-label="Begin the journey from Chapter 01 — The Why"
              data-testid="story-plaque-begin-journey"
            >
              <div className="story-fivew-plaque-inner">
                <span className="story-fivew-eyebrow">Step inside</span>
                <h2 className="story-fivew-brand">CADENCE Medical Consulting</h2>
                <span className="story-fivew-divider" aria-hidden="true" />
                <span className="story-fivew-subtitle">The 5 W&rsquo;s</span>
                <span className="story-fivew-footnote">where it all converges</span>
                <span className="story-fivew-plaque-hint">Step in with us &rarr;</span>
              </div>
            </button>
          </div>
          <div className="story-playbills">
            {storyArcs.map((arc, idx) => {
              // If label has em-dash, split into "prefix" (the W) + "descriptor".
              // The descriptor may contain a "|" to force a deliberate line break
              // (e.g., "Long Overdue,|Now Delivered" stacks as two lines).
              const [prefix, descriptorRaw] = arc.label.includes(' \u2014 ')
                ? arc.label.split(' \u2014 ')
                : [arc.label, null];
              const descriptorLines = descriptorRaw ? descriptorRaw.split('|') : null;
              return (
                <button
                  key={arc.id}
                  className="story-playbill"
                  onClick={() => openChapter(idx)}
                  data-testid={`story-playbill-${arc.id}`}
                  aria-label={`Open chapter: ${arc.label.replace('|', ' ')}`}
                >
                  <span className="story-playbill-num">Chapter {String(idx + 1).padStart(2, '0')}</span>
                  <span className="story-playbill-rule" aria-hidden="true" />
                  <span className="story-playbill-label">
                    <span className="story-playbill-label-prefix">{prefix}</span>
                    {descriptorLines && (
                      <span className="story-playbill-label-descriptor">
                        {descriptorLines.map((line, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && <br />}
                            {line}
                          </React.Fragment>
                        ))}
                      </span>
                    )}
                  </span>
                  <span className="story-playbill-cta">Play Scene <ArrowRight size={14} /></span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Closing copper tile — uses the canonical site-wide pattern
          (.clarity-copper-tile + .clarity-tile-text) so it matches the
          closing tile on Home, CADENCE Promise, etc. */}
      <section className="clarity-tile-section" data-testid="story-closing-tile">
        <div className="container">
          <div className="clarity-copper-tile">
            <p className="clarity-tile-text">
              Your health story is in your records.
            </p>
          </div>
        </div>
      </section>

      {/* Slim CTA on satin — same pattern as other pages */}
      <section className="story-slim-cta-section" data-testid="story-slim-cta">
        <div className="container">
          <div className="story-slim-cta-bar">
            <span className="story-slim-cta-text">Ready to be heard?</span>
            <Link to="/contact" className="story-slim-cta-btn" data-testid="story-cta">
              Schedule a Call
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Back nav */}
      <section className="story-back-section">
        <Link to="/founders" className="story-back-link">
          <ArrowLeft size={16} />
          Back to Founders
        </Link>
      </section>

      {/* Footer rendered globally by App.js — no per-page <Footer/> here */}

      {/* ===================================================
          CINEMATIC REEL OVERLAY — fullscreen black velvet
          stage. Renders only when a chapter is active.
          =================================================== */}
      {activeArc && (
        <div
          className="story-reel-overlay"
          data-testid="story-reel-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`Chapter: ${activeArc.label.replace('|', ' ')}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            type="button"
            className="story-reel-close"
            onClick={closeReel}
            aria-label="Close chapter"
            data-testid="story-reel-close"
          >
            <X size={22} />
          </button>

          {/* Reel header — chapter number + progress dots */}
          <div className="story-reel-header">
            <span className="story-reel-chapter-num">
              Chapter {String(activeIndex + 1).padStart(2, '0')}
              <span className="story-reel-chapter-of"> of {String(storyArcs.length).padStart(2, '0')}</span>
            </span>
            <div className="story-reel-dots" aria-hidden="true">
              {storyArcs.map((arc, i) => (
                <span
                  key={arc.id}
                  className={`story-reel-dot ${i === activeIndex ? 'is-active' : ''} ${i < activeIndex ? 'is-past' : ''}`}
                />
              ))}
            </div>
          </div>

          {/* Scene content (re-mounts on chapter change via key) */}
          <div className="story-reel-scene" key={sceneKey} data-testid="story-reel-scene">
            {/* Inside the scene we show just the W question (Why? What?
                When & Who? But How?) as the headline — bigger, simpler,
                cleaner than repeating the full playbill label. The
                descriptor reads as a quiet subtitle beneath. */}
            <h2 className="story-reel-label">
              <span className="story-reel-label-ornament" aria-hidden="true" />
              {(() => {
                const cleanLabel = activeArc.label.replace('|', ' ');
                const [prefix, descriptor] = cleanLabel.includes(' \u2014 ')
                  ? cleanLabel.split(' \u2014 ')
                  : [cleanLabel, null];
                // Strip "The " and append "?" for The What / The Why so it
                // reads as a question. "When & Who" + "But How" already
                // function as questions on their own — append "?" too.
                const headline = prefix.replace(/^The\s+/i, '').toUpperCase() + '?';
                return (
                  <>
                    <span className="story-reel-label-question">{headline}</span>
                    {descriptor && (
                      <span className="story-reel-label-answer">{descriptor}</span>
                    )}
                  </>
                );
              })()}
              <span className="story-reel-label-ornament" aria-hidden="true" />
            </h2>

            {activeArc.highlight && (
              <blockquote className="story-reel-highlight">
                {activeArc.highlight}
              </blockquote>
            )}

            <ReelListenButton
              key={`listen-${activeIndex}`}
              text={chapterNarration(activeArc)}
              autoStart={narrating}
              onStart={() => setNarrating(true)}
              onEnded={handleChapterEnded}
              autoplayEnabled={autoplay}
              onToggleAutoplay={toggleAutoplay}
              label="Listen to this chapter"
              autoplayLabel="Auto-play chapters"
              testId="story-reel-listen"
            />

            <div className="story-reel-narrative">
              {activeArc.paragraphs.map((p, idx) => (
                <p
                  key={idx}
                  className="story-reel-paragraph"
                  style={{ animationDelay: `${0.7 + idx * 0.25}s` }}
                >
                  {p}
                </p>
              ))}
            </div>

            {activeArc.quote && (
              <blockquote className="story-reel-quote" data-testid="story-reel-quote">
                <p className="story-reel-quote-text">{activeArc.quote}</p>
                <span className="story-reel-quote-attr">{activeArc.quoteAttr}</span>
              </blockquote>
            )}
          </div>

          {/* Floating side-edge arrows — sticky to viewport edges so
              the visitor can navigate chapters WITHOUT scrolling to
              the bottom (user feedback 2026-02 — long chapters on
              mobile pushed the Continue button off-screen). Always
              visible; tap target padded for thumb-reach. */}
          <button
            type="button"
            className="story-reel-side-arrow story-reel-side-arrow--prev"
            onClick={prevChapter}
            disabled={activeIndex === 0}
            aria-label="Previous chapter"
            data-testid="story-reel-side-prev"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            className="story-reel-side-arrow story-reel-side-arrow--next"
            onClick={nextChapter}
            aria-label={activeIndex === storyArcs.length - 1 ? 'Close reel' : 'Next chapter'}
            data-testid="story-reel-side-next"
          >
            <ArrowRight size={20} strokeWidth={2.5} />
          </button>

          {/* Navigation arrows */}
          <div className="story-reel-nav">
            <button
              type="button"
              className="story-reel-nav-btn prev"
              onClick={prevChapter}
              disabled={activeIndex === 0}
              data-testid="story-reel-prev"
              aria-label="Previous chapter"
            >
              <ArrowLeft size={16} />
              <span>Previous</span>
            </button>

            <button
              type="button"
              className="story-reel-nav-btn next"
              onClick={nextChapter}
              data-testid="story-reel-next"
              aria-label={activeIndex === storyArcs.length - 1 ? 'Close reel' : 'Next chapter'}
            >
              <span>
                {activeIndex === storyArcs.length - 1 ? 'Close Reel' : 'Continue'}
              </span>
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OurStory;
